import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useNotifySubscription } from '../../hooks/useNotifySubscription';
import { TEST_IDS } from '../../utils/testIds';
/**
 * Email signup that swaps in for Add-to-Cart when a product is in_development.
 *
 * Idempotent on the server side — re-submitting the same email is fine and produces an
 * "already on the list" success state instead of an error. Server lowercases the email.
 */ export default function NotifySignup({ product, variant = 'panel', tone = 'dark', className = '' }) {
    const { state, errorMessage, subscriberCount, submit } = useNotifySubscription(product.id, product.slug);
    const [email, setEmail] = useState('');
    const onSubmit = (e)=>{
        e.preventDefault();
        if (state === 'submitting') return;
        submit(email.trim());
    };
    // Treat both `success` and `already` as "you're on the list" — copy distinguishes.
    const subscribed = state === 'success' || state === 'already';
    const liveCount = subscriberCount ?? product.notifySubscriberCount;
    const isLight = tone === 'light';
    const labelColor = isLight ? 'text-bone-200' : 'text-obsidian-700';
    const valueColor = isLight ? 'text-bone-50' : 'text-obsidian-900';
    const inputBase = isLight ? 'bg-obsidian-900/40 border-obsidian-700 text-bone-50 placeholder:text-bone-200/40 focus:border-clay-400' : 'bg-bone-50 border-dune-300 text-obsidian-900 placeholder:text-obsidian-400 focus:border-clay-500';
    const buttonBase = isLight ? 'bg-clay-500 text-bone-50 hover:bg-clay-400' : 'bg-obsidian-900 text-bone-50 hover:bg-clay-600';
    if (variant === 'inline') {
        return /*#__PURE__*/ _jsx("form", {
            onSubmit: onSubmit,
            className: `flex w-full gap-2 ${className}`,
            noValidate: true,
            children: subscribed ? /*#__PURE__*/ _jsx("p", {
                "data-testid": TEST_IDS.pdpNotifySuccessMessage,
                className: `flex-1 text-[11px] uppercase tracking-[0.24em] font-medium px-3 py-3 ${isLight ? 'text-sage-300' : 'text-clay-600'}`,
                "aria-live": "polite",
                children: state === 'already' ? 'Already on the list.' : "You're on the list."
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("input", {
                        type: "email",
                        "data-testid": TEST_IDS.pdpNotifyEmailInput,
                        required: true,
                        autoComplete: "email",
                        placeholder: "you@email.com",
                        value: email,
                        onChange: (e)=>setEmail(e.target.value),
                        className: `flex-1 px-3 py-3 text-sm border-2 outline-none transition-colors ${inputBase}`,
                        disabled: state === 'submitting',
                        "aria-label": "Email for launch notification"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "submit",
                        "data-testid": TEST_IDS.pdpNotifySubmitButton,
                        disabled: state === 'submitting',
                        className: `px-4 py-3 text-[11px] font-medium uppercase tracking-[0.24em] transition-colors disabled:opacity-60 ${buttonBase}`,
                        children: state === 'submitting' ? 'Sending…' : 'Notify me'
                    })
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: className,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-3 mb-5",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: `h-px w-10 ${isLight ? 'bg-clay-400' : 'bg-clay-500'}`,
                        "aria-hidden": true
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        "data-testid": TEST_IDS.pdpInDevelopmentBadge,
                        className: `font-mono text-[10px] uppercase tracking-[0.35em] font-medium ${isLight ? 'text-ember-300' : 'text-clay-600'}`,
                        children: "In development"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                className: `text-base leading-[1.65] ${labelColor}`,
                children: "Drop your email to be the first to know when this ships. No marketing emails — only a launch notice when it's time to order."
            }),
            subscribed ? /*#__PURE__*/ _jsxs("div", {
                "data-testid": TEST_IDS.pdpNotifySuccessMessage,
                className: "mt-7",
                "aria-live": "polite",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: `font-display text-2xl ${valueColor}`,
                        style: {
                            fontWeight: 500,
                            letterSpacing: '-0.01em'
                        },
                        children: state === 'already' ? "You're already on the list." : "You're on the list."
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: `mt-3 text-sm leading-[1.6] ${labelColor}`,
                        children: "We'll send a single email the day it ships. Thanks for being early."
                    })
                ]
            }) : /*#__PURE__*/ _jsxs("form", {
                onSubmit: onSubmit,
                className: "mt-7 flex flex-col gap-3 sm:flex-row",
                noValidate: true,
                children: [
                    /*#__PURE__*/ _jsx("input", {
                        type: "email",
                        "data-testid": TEST_IDS.pdpNotifyEmailInput,
                        required: true,
                        autoComplete: "email",
                        placeholder: "you@email.com",
                        value: email,
                        onChange: (e)=>setEmail(e.target.value),
                        className: `flex-1 px-4 py-4 text-sm border-2 outline-none transition-colors ${inputBase}`,
                        disabled: state === 'submitting',
                        "aria-label": "Email for launch notification"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "submit",
                        "data-testid": TEST_IDS.pdpNotifySubmitButton,
                        disabled: state === 'submitting',
                        className: `px-6 py-4 text-[11px] font-medium uppercase tracking-[0.28em] transition-colors disabled:opacity-60 ${buttonBase}`,
                        children: state === 'submitting' ? 'Sending…' : 'Notify me'
                    })
                ]
            }),
            state === 'error' && /*#__PURE__*/ _jsx("p", {
                className: "mt-3 text-sm text-red-400",
                "aria-live": "polite",
                children: errorMessage
            }),
            liveCount > 0 && !subscribed && /*#__PURE__*/ _jsxs("p", {
                className: `mt-5 font-mono text-[11px] uppercase tracking-[0.28em] ${isLight ? 'text-bone-200/70' : 'text-obsidian-500'}`,
                children: [
                    liveCount,
                    " ",
                    liveCount === 1 ? 'person' : 'people',
                    " on the list"
                ]
            })
        ]
    });
}
